import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { definePreset } from '@primeuix/themes';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';

import Aura from '@primeuix/themes/aura';

import { routes } from './app.routes';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { ErrorHandler } from '@angular/core';
import { GlobalErrorHandler } from './core/global-error-handler';

// Primeng 20 Theme - EBBA Design System
const projectTheme = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '4px',
      sm: '8px',
      md: '16px',
      lg: '24px',
      xl: '32px',
    },
    // EBBA Purple Color Palette
    purple: {
      50: '#F1ECFE',
      100: '#E3D8FD',
      200: '#C7B1FB',
      300: '#AB8AF9',
      400: '#8F63F7',
      500: '#8050F6',
      600: '#6B3DD8',
      700: '#562AB9',
      800: '#400AC2',
      900: '#270677',
      950: '#200561',
    },
    // EBBA Gray Scale
    gray: {
      50: '#FFFFFF',
      100: '#F7F7F5',
      200: '#EBEBEB',
      300: '#E0E0E0',
      400: '#CCCCCC',
      500: '#A3A3A3',
      600: '#525252',
      700: '#212121',
      800: '#1A1A1A',
      900: '#0F0F0F',
      950: '#000000',
    },
    // Keep only essential colors for PrimeNG components
    red: {
      400: '#EF5350',
      600: '#E53935',
    },
  },
  semantic: {
    transitionDuration: '0.3s',
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.color}',
      offset: '2px',
      shadow: 'none',
    },
    disabledOpacity: '0.6',
    iconSize: '1rem',
    anchorGutter: '2px',
    // EBBA Primary Colors
    primary: {
      50: '#F1ECFE',
      100: '#E3D8FD',
      200: '#C7B1FB',
      300: '#AB8AF9',
      400: '#8F63F7',
      500: '#8050F6',
      600: '#6B3DD8',
      700: '#562AB9',
      800: '#400AC2',
      900: '#270677',
      950: '#200561',
    },
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      sm: {
        fontSize: '0.875rem',
        paddingX: '0.625rem',
        paddingY: '0.375rem',
      },
      lg: {
        fontSize: '1.125rem',
        paddingX: '0.875rem',
        paddingY: '0.625rem',
      },
      borderRadius: '{border.radius.md}',
      focusRing: {
        width: '2px',
        style: 'solid',
        color: '{primary.color}',
        offset: '2px',
        shadow: 'none',
      },
      transitionDuration: '{transition.duration}',
    },
    list: {
      padding: '0.25rem 0.25rem',
      gap: '2px',
      header: {
        padding: '0.5rem 1rem 0.25rem 1rem',
      },
      option: {
        padding: '0.5rem 0.75rem',
        borderRadius: '{border.radius.sm}',
      },
      optionGroup: {
        padding: '0.5rem 0.75rem',
        fontWeight: '600',
      },
    },
    content: {
      borderRadius: '{border.radius.md}',
    },
    mask: {
      transitionDuration: '0.15s',
    },
    navigation: {
      list: {
        padding: '0.25rem 0.25rem',
        gap: '2px',
      },
      item: {
        padding: '0.5rem 0.75rem',
        borderRadius: '{border.radius.sm}',
        gap: '0.5rem',
      },
      submenuLabel: {
        padding: '0.5rem 0.75rem',
        fontWeight: '600',
      },
      submenuIcon: {
        size: '0.875rem',
      },
    },
    overlay: {
      select: {
        borderRadius: '{border.radius.md}',
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      },
      popover: {
        borderRadius: '{border.radius.md}',
        padding: '0.75rem',
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      },
      modal: {
        borderRadius: '{border.radius.xl}',
        padding: '1.25rem',
        shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      },
      navigation: {
        shadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      },
    },
    // colorScheme: {
    //   light: {
    //     surface: {
    //       0: '#FFFFFF',
    //       50: '#F7F7F5',
    //       100: '#EBEBEB',
    //       200: '#E0E0E0',
    //       300: '#CCCCCC',
    //       400: '#A3A3A3',
    //       500: '#525252',
    //       600: '#212121',
    //       700: '#1A1A1A',
    //       800: '#0F0F0F',
    //       900: '#000000',
    //       950: '#000000',
    //     },
    //     primary: {
    //       color: '{primary.900}',
    //       contrastColor: '#ffffff',
    //       hoverColor: '{primary.800}',
    //       activeColor: '{primary.700}',
    //     },
    //     highlight: {
    //       background: '{primary.50}',
    //       focusBackground: '{primary.100}',
    //       color: '{primary.700}',
    //       focusColor: '{primary.800}',
    //     },
    //     mask: {
    //       background: 'rgba(0,0,0,0.4)',
    //       color: '{surface.200}',
    //     },
    //     formField: {
    //       background: '{surface.0}',
    //       disabledBackground: '{surface.200}',
    //       filledBackground: '{surface.50}',
    //       filledHoverBackground: '{surface.50}',
    //       filledFocusBackground: '{surface.50}',
    //       borderColor: '{surface.300}',
    //       hoverBorderColor: '{surface.400}',
    //       focusBorderColor: '{primary.color}',
    //       invalidBorderColor: '{red.400}',
    //       color: '{surface.700}',
    //       disabledColor: '{surface.500}',
    //       placeholderColor: '{surface.500}',
    //       invalidPlaceholderColor: '{red.600}',
    //       floatLabelColor: '{surface.500}',
    //       floatLabelFocusColor: '{primary.600}',
    //       floatLabelActiveColor: '{surface.500}',
    //       floatLabelInvalidColor: '{form.field.invalid.placeholder.color}',
    //       iconColor: '{surface.400}',
    //       shadow: '0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(18, 18, 23, 0.05)',
    //     },
    //     text: {
    //       color: '{surface.700}',
    //       hoverColor: '{surface.800}',
    //       mutedColor: '{surface.500}',
    //       hoverMutedColor: '{surface.600}',
    //     },
    //     content: {
    //       background: '{surface.0}',
    //       hoverBackground: '{surface.100}',
    //       borderColor: '{surface.200}',
    //       color: '{text.color}',
    //       hoverColor: '{text.hover.color}',
    //     },
    //     overlay: {
    //       select: {
    //         background: '{surface.0}',
    //         borderColor: '{surface.200}',
    //         color: '{text.color}',
    //       },
    //       popover: {
    //         background: '{surface.0}',
    //         borderColor: '{surface.200}',
    //         color: '{text.color}',
    //       },
    //       modal: {
    //         background: '{surface.0}',
    //         borderColor: '{surface.200}',
    //         color: '{text.color}',
    //       },
    //     },
    //     list: {
    //       option: {
    //         focusBackground: '{surface.100}',
    //         selectedBackground: '{highlight.background}',
    //         selectedFocusBackground: '{highlight.focus.background}',
    //         color: '{text.color}',
    //         focusColor: '{text.hover.color}',
    //         selectedColor: '{highlight.color}',
    //         selectedFocusColor: '{highlight.focus.color}',
    //         icon: {
    //           color: '{surface.400}',
    //           focusColor: '{surface.500}',
    //         },
    //       },
    //       optionGroup: {
    //         background: 'transparent',
    //         color: '{text.muted.color}',
    //       },
    //     },
    //     navigation: {
    //       item: {
    //         focusBackground: '{surface.100}',
    //         activeBackground: '{surface.100}',
    //         color: '{text.color}',
    //         focusColor: '{text.hover.color}',
    //         activeColor: '{text.hover.color}',
    //         icon: {
    //           color: '{surface.400}',
    //           focusColor: '{surface.500}',
    //           activeColor: '{surface.500}',
    //         },
    //       },
    //       submenuLabel: {
    //         background: 'transparent',
    //         color: '{text.muted.color}',
    //       },
    //       submenuIcon: {
    //         color: '{surface.400}',
    //         focusColor: '{surface.500}',
    //         activeColor: '{surface.500}',
    //       },
    //     },
    //   },
    //   dark: {
    //     surface: {
    //       0: '#FFFFFF',
    //       50: '#F7F7F5',
    //       100: '#EBEBEB',
    //       200: '#E0E0E0',
    //       300: '#CCCCCC',
    //       400: '#A3A3A3',
    //       500: '#525252',
    //       600: '#212121',
    //       700: '#1A1A1A',
    //       800: '#0F0F0F',
    //       900: '#000000',
    //       950: '#000000',
    //     },
    //     primary: {
    //       color: '{primary.900}',
    //       contrastColor: '#ffffff',
    //       hoverColor: '{primary.800}',
    //       activeColor: '{primary.700}',
    //     },
    //     highlight: {
    //       background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
    //       focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
    //       color: 'rgba(255,255,255,.87)',
    //       focusColor: 'rgba(255,255,255,.87)',
    //     },
    //     mask: {
    //       background: 'rgba(0,0,0,0.6)',
    //       color: '{surface.200}',
    //     },
    //     formField: {
    //       background: '{surface.950}',
    //       disabledBackground: '{surface.700}',
    //       filledBackground: '{surface.800}',
    //       filledHoverBackground: '{surface.800}',
    //       filledFocusBackground: '{surface.800}',
    //       borderColor: '{surface.600}',
    //       hoverBorderColor: '{surface.500}',
    //       focusBorderColor: '{primary.color}',
    //       invalidBorderColor: '{red.300}',
    //       color: '{surface.0}',
    //       disabledColor: '{surface.400}',
    //       placeholderColor: '{surface.400}',
    //       invalidPlaceholderColor: '{red.400}',
    //       floatLabelColor: '{surface.400}',
    //       floatLabelFocusColor: '{primary.color}',
    //       floatLabelActiveColor: '{surface.400}',
    //       floatLabelInvalidColor: '{form.field.invalid.placeholder.color}',
    //       iconColor: '{surface.400}',
    //       shadow: '0 0 #0000, 0 0 #0000, 0 1px 2px 0 rgba(18, 18, 23, 0.05)',
    //     },
    //     text: {
    //       color: '{surface.0}',
    //       hoverColor: '{surface.0}',
    //       mutedColor: '{surface.400}',
    //       hoverMutedColor: '{surface.300}',
    //     },
    //     content: {
    //       background: '{surface.900}',
    //       hoverBackground: '{surface.800}',
    //       borderColor: '{surface.700}',
    //       color: '{text.color}',
    //       hoverColor: '{text.hover.color}',
    //     },
    //     overlay: {
    //       select: {
    //         background: '{surface.900}',
    //         borderColor: '{surface.700}',
    //         color: '{text.color}',
    //       },
    //       popover: {
    //         background: '{surface.900}',
    //         borderColor: '{surface.700}',
    //         color: '{text.color}',
    //       },
    //       modal: {
    //         background: '{surface.900}',
    //         borderColor: '{surface.700}',
    //         color: '{text.color}',
    //       },
    //     },
    //     list: {
    //       option: {
    //         focusBackground: '{surface.800}',
    //         selectedBackground: '{highlight.background}',
    //         selectedFocusBackground: '{highlight.focus.background}',
    //         color: '{text.color}',
    //         focusColor: '{text.hover.color}',
    //         selectedColor: '{highlight.color}',
    //         selectedFocusColor: '{highlight.focus.color}',
    //         icon: {
    //           color: '{surface.500}',
    //           focusColor: '{surface.400}',
    //         },
    //       },
    //       optionGroup: {
    //         background: 'transparent',
    //         color: '{text.muted.color}',
    //       },
    //     },
    //     navigation: {
    //       item: {
    //         focusBackground: '{surface.800}',
    //         activeBackground: '{surface.800}',
    //         color: '{text.color}',
    //         focusColor: '{text.hover.color}',
    //         activeColor: '{text.hover.color}',
    //         icon: {
    //           color: '{surface.500}',
    //           focusColor: '{surface.400}',
    //           activeColor: '{surface.400}',
    //         },
    //       },
    //       submenuLabel: {
    //         background: 'transparent',
    //         color: '{text.muted.color}',
    //       },
    //       submenuIcon: {
    //         color: '{surface.500}',
    //         focusColor: '{surface.400}',
    //         activeColor: '{surface.400}',
    //       },
    //     },
    //   },
    // },
    colorScheme: {
      
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
      })
    ),
    provideClientHydration(
      withEventReplay(),
      withIncrementalHydration(),
      withHttpTransferCacheOptions({
        filter: (req) =>
          req.url.includes('.gif') ||
          req.url.includes('.png') ||
          req.url.includes('.jpg') ||
          req.url.includes('.jpeg') ||
          req.url.includes('.svg') ||
          req.url.includes('.webp'),
      })
    ),
    provideHttpClient(withFetch()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: projectTheme,
        options: {
          darkModeSelector: false || 'none',
        },
      },
    }),
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    MessageService,
  ],
};
